import React, { useState, useEffect } from "react";
import { PaymentOption } from "./types";
import { sanitizeInput } from "./utils/sanitize";
import { useTranslation } from "./contexts/LocaleContext";
import { invoiceTranslations } from "./translations/invoiceTranslations";
import { useLocale } from "./contexts/LocaleContext";
import { Pipette, Trash2, AlertTriangle } from "lucide-react";

type SavedPaymentMethod = PaymentOption;

type PaymentMethodDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  paymentMethods: SavedPaymentMethod[];
  onAddPaymentMethod: (method: Omit<SavedPaymentMethod, "id">) => void;
  onUpdatePaymentMethod: (
    id: string,
    method: Omit<SavedPaymentMethod, "id">
  ) => void;
  onDeletePaymentMethod: (id: string) => void;
  initialData?: Partial<SavedPaymentMethod>;
  zIndex?: number;
};

export default function PaymentMethodDialog({
  isOpen,
  onClose,
  paymentMethods,
  onAddPaymentMethod,
  onUpdatePaymentMethod,
  onDeletePaymentMethod,
  initialData,
  zIndex = 200,
}: PaymentMethodDialogProps) {
  const t = useTranslation();
  const { locale } = useLocale();
  const [editingMethod, setEditingMethod] = useState<SavedPaymentMethod | null>(
    null
  );
  const [formData, setFormData] = useState<Omit<SavedPaymentMethod, "id">>({
    type: "bank",
    title: "",
    details: "",
  });

  // Set initial data when modal opens
  useEffect(() => {
    if (isOpen && initialData && !editingMethod) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        type: initialData.type || prev.type,
        title: initialData.title || prev.title,
        details: initialData.details || prev.details,
      }));
    }
  }, [isOpen, initialData, editingMethod]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Get invoice translations for current locale
  const currentInvoiceT =
    invoiceTranslations[locale === "en" ? "en" : locale === "cs" ? "cs" : "ru"];

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!isFormValid()) {
      return;
    }

    // Auto-generate details for bank and link types if not manually set
    const submissionData = { ...formData };

    if (submissionData.type === "bank") {
      // Auto-generate bank details text using translations
      let autoDetails = "";
      if (submissionData.accountNumber)
        autoDetails += `${t.paymentMethodDialog.accountNumber}: ${submissionData.accountNumber}\n`;
      if (submissionData.iban)
        autoDetails += `${t.paymentMethodDialog.iban}: ${submissionData.iban}\n`;
      if (submissionData.bic)
        autoDetails += `${t.paymentMethodDialog.bicSwift}: ${submissionData.bic}\n`;
      if (submissionData.bankName)
        autoDetails += `${t.paymentMethodDialog.bankName}: ${submissionData.bankName}\n`;
      if (submissionData.bankAddress)
        autoDetails += `${submissionData.bankAddress}\n`;
      if (submissionData.variableSymbol)
        autoDetails += `${t.paymentMethodDialog.variableSymbol}: ${submissionData.variableSymbol}\n`;
      if (submissionData.constantSymbol)
        autoDetails += `${t.paymentMethodDialog.constantSymbol}: ${submissionData.constantSymbol}\n`;
      if (submissionData.specificSymbol)
        autoDetails += `${t.paymentMethodDialog.specificSymbol}: ${submissionData.specificSymbol}`;
      submissionData.details = autoDetails.trim();
    } else if (submissionData.type === "link" && submissionData.paymentUrl) {
      // Auto-generate link details using translations
      submissionData.details = `${currentInvoiceT.paymentLink}: ${submissionData.paymentUrl}`;
    }

    if (editingMethod) {
      onUpdatePaymentMethod(editingMethod.id, submissionData);
    } else {
      onAddPaymentMethod(submissionData);
    }
    handleCancel();
  };

  const handleEdit = (method: SavedPaymentMethod) => {
    setEditingMethod(method);
    setFormData({
      type: method.type,
      title: method.title,
      details: method.details,
      accountNumber: method.accountNumber,
      iban: method.iban,
      bic: method.bic,
      bankName: method.bankName,
      bankAddress: method.bankAddress,
      variableSymbol: method.variableSymbol,
      constantSymbol: method.constantSymbol,
      specificSymbol: method.specificSymbol,
      paymentUrl: method.paymentUrl,
    });
  };

  const handleCancel = () => {
    setEditingMethod(null);
    setFormData({
      type: "bank",
      title: "",
      details: "",
    });
  };

  const handleDelete = (id: string) => {
    if (window.confirm(t.paymentMethodDialog.deleteConfirm)) {
      onDeletePaymentMethod(id);
    }
  };

  const handleTypeChange = (type: PaymentOption["type"]) => {
    setFormData((prev) => ({
      ...prev,
      type,
      // Clear fields that don't apply to the new type
      accountNumber: type === "bank" ? prev.accountNumber : undefined,
      iban: type === "bank" ? prev.iban : undefined,
      bic: type === "bank" ? prev.bic : undefined,
      bankName: type === "bank" ? prev.bankName : undefined,
      bankAddress: type === "bank" ? prev.bankAddress : undefined,
      variableSymbol: type === "bank" ? prev.variableSymbol : undefined,
      constantSymbol: type === "bank" ? prev.constantSymbol : undefined,
      specificSymbol: type === "bank" ? prev.specificSymbol : undefined,
      paymentUrl: type === "link" ? prev.paymentUrl : undefined,
    }));
  };

  const getTitlePlaceholder = () => {
    if (formData.type === "bank")
      return t.paymentMethodDialog.titlePlaceholderBank;
    if (formData.type === "link")
      return t.paymentMethodDialog.titlePlaceholderLink;
    if (formData.type === "cash")
      return t.paymentMethodDialog.titlePlaceholderCash;
    return t.paymentMethodDialog.titlePlaceholderCustom;
  };

  const getDetailsPlaceholder = () => {
    if (formData.type === "cash")
      return t.paymentMethodDialog.detailsPlaceholderCash;
    if (formData.type === "custom")
      return t.paymentMethodDialog.detailsPlaceholderCustom;
    return "";
  };

  const getTypeLabel = (type: string) => {
    if (type === "bank") return t.paymentMethodDialog.bankTransfer;
    if (type === "link") return t.paymentMethodDialog.paymentLink;
    if (type === "cash") return t.paymentMethodDialog.cash;
    return t.paymentMethodDialog.custom;
  };

  // Validate form: title must be filled and at least one other field must be filled
  const isFormValid = () => {
    if (!formData.title || formData.title.trim() === "") {
      return false;
    }

    // Check if at least one other field is filled based on type
    if (formData.type === "bank") {
      return !!(
        formData.accountNumber ||
        formData.iban ||
        formData.bic ||
        formData.bankName ||
        formData.bankAddress ||
        formData.variableSymbol ||
        formData.constantSymbol ||
        formData.specificSymbol
      );
    } else if (formData.type === "link") {
      return !!formData.paymentUrl;
    } else if (formData.type === "cash" || formData.type === "custom") {
      return !!formData.details;
    }

    return false;
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if click was directly on backdrop, not on child elements
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
      style={{ zIndex }}
      onClick={handleBackdropClick}
      onMouseDown={(e) => {
        // Prevent closing when selecting text
        if (e.target !== e.currentTarget) {
          e.stopPropagation();
        }
      }}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-200/50 dark:border-slate-700/50 transition-colors"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t.paymentMethodDialog.manageTitle}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Saved Payment Methods Section - moved to top */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {t.paymentMethodDialog.savedMethods}
            </h3>
            {paymentMethods.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400">
                {t.paymentMethodDialog.noMethods}
              </p>
            ) : (
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <div className="grid grid-cols-4 gap-4 p-3 bg-slate-50 dark:bg-slate-900 font-semibold text-sm text-slate-700 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700">
                  <div>{t.paymentMethodDialog.type}</div>
                  <div>{t.paymentMethodDialog.titleLabel}</div>
                  <div>{t.paymentMethodDialog.details}</div>
                  <div>{t.common.actions}</div>
                </div>
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className="grid grid-cols-4 gap-4 p-3 border-b border-slate-200 dark:border-slate-700 last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors"
                  >
                    <div>
                      <span className="inline-block px-2 py-1 rounded text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                        {getTypeLabel(method.type)}
                      </span>
                    </div>
                    <div className="font-medium text-slate-900 dark:text-slate-100">
                      {method.title}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {method.type === "bank" &&
                        method.accountNumber &&
                        `${t.paymentMethodDialog.accountNumber}: ${method.accountNumber}`}
                      {method.type === "bank" &&
                        method.iban &&
                        ` | ${t.paymentMethodDialog.iban}: ${method.iban}`}
                      {method.type === "link" &&
                        method.paymentUrl &&
                        method.paymentUrl}
                      {(method.type === "cash" || method.type === "custom") &&
                        method.details &&
                        method.details.substring(0, 50) +
                          (method.details.length > 50 ? "..." : "")}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(method)}
                        className="p-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded transition-colors flex items-center justify-center"
                        title={t.common.edit}
                      >
                        <Pipette className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(method.id)}
                        className="p-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded transition-colors flex items-center justify-center"
                        title={t.common.delete}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add/Edit Payment Method Form */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {editingMethod
                ? t.paymentMethodDialog.editTitle
                : t.paymentMethodDialog.addTitle}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t.paymentMethodDialog.type}
                </label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    handleTypeChange(e.target.value as PaymentOption["type"])
                  }
                  required
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="bank">
                    {t.paymentMethodDialog.bankTransfer}
                  </option>
                  <option value="link">
                    {t.paymentMethodDialog.paymentLink}
                  </option>
                  <option value="cash">{t.paymentMethodDialog.cash}</option>
                  <option value="custom">{t.paymentMethodDialog.custom}</option>
                </select>
              </div>

              {/* Add Payment Method button - only show when not editing */}
              {!editingMethod && (
                <div className="space-y-2">
                  <button
                    type="submit"
                    disabled={!isFormValid()}
                    className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-slate-600 dark:disabled:text-slate-400 text-white rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
                  >
                    {t.paymentMethodDialog.add}
                  </button>
                  {!isFormValid() && (
                    <div className="px-4 py-2 bg-amber-100/60 dark:bg-amber-900/30 border border-amber-400 dark:border-amber-500/50 rounded-lg flex items-center gap-2 text-amber-800 dark:text-amber-400 text-sm shadow-sm">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span>{t.paymentMethodDialog.validationMessage}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Title and Account Number in one row on desktop */}
              {formData.type === "bank" ? (
                <div className="grid grid-cols-1 min-[680px]:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t.paymentMethodDialog.titleLabel}
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          title: sanitizeInput(e.target.value),
                        }))
                      }
                      placeholder={getTitlePlaceholder()}
                      required
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t.paymentMethodDialog.accountNumber}
                    </label>
                    <input
                      type="text"
                      value={formData.accountNumber || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          accountNumber: sanitizeInput(e.target.value),
                        }))
                      }
                      placeholder="123456789/0100"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t.paymentMethodDialog.titleLabel}
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        title: sanitizeInput(e.target.value),
                      }))
                    }
                    placeholder={getTitlePlaceholder()}
                    required
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}

              {formData.type === "bank" && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t.paymentMethodDialog.iban}
                      </label>
                      <input
                        type="text"
                        value={formData.iban || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            iban: sanitizeInput(e.target.value),
                          }))
                        }
                        placeholder="CZ65 0800 0000 1920 0014 5399"
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t.paymentMethodDialog.bicSwift}
                      </label>
                      <input
                        type="text"
                        value={formData.bic || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            bic: sanitizeInput(e.target.value),
                          }))
                        }
                        placeholder="GIBACZPX"
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t.paymentMethodDialog.bankName}
                    </label>
                    <input
                      type="text"
                      value={formData.bankName || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          bankName: sanitizeInput(e.target.value),
                        }))
                      }
                      placeholder={t.paymentMethodDialog.bankName}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t.paymentMethodDialog.bankAddress}
                    </label>
                    <textarea
                      value={formData.bankAddress || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          bankAddress: sanitizeInput(e.target.value),
                        }))
                      }
                      placeholder={t.paymentMethodDialog.bankAddress}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 min-h-[80px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  {/* Symbols in one row on desktop, wrap on mobile */}
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t.paymentMethodDialog.variableSymbol}
                      </label>
                      <input
                        type="text"
                        value={formData.variableSymbol || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            variableSymbol: sanitizeInput(e.target.value),
                          }))
                        }
                        placeholder="123456"
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t.paymentMethodDialog.constantSymbol}
                      </label>
                      <input
                        type="text"
                        value={formData.constantSymbol || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            constantSymbol: sanitizeInput(e.target.value),
                          }))
                        }
                        placeholder="0308"
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t.paymentMethodDialog.specificSymbol}
                      </label>
                      <input
                        type="text"
                        value={formData.specificSymbol || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            specificSymbol: sanitizeInput(e.target.value),
                          }))
                        }
                        placeholder="1234"
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                </>
              )}

              {formData.type === "link" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t.paymentMethodDialog.paymentUrl}
                  </label>
                  <input
                    type="url"
                    value={formData.paymentUrl || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        paymentUrl: sanitizeInput(e.target.value),
                      }))
                    }
                    placeholder="https://payment.example.com"
                    required
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}

              {(formData.type === "cash" || formData.type === "custom") && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t.paymentMethodDialog.details}
                  </label>
                  <textarea
                    value={formData.details}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        details: sanitizeInput(e.target.value),
                      }))
                    }
                    placeholder={getDetailsPlaceholder()}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 min-h-[80px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}

              {/* Only show buttons when editing */}
              {editingMethod && (
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
                  >
                    {t.paymentMethodDialog.update}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg font-medium transition-colors"
                  >
                    {t.common.cancel}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
